import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { CreditCardIcon, SquareCheckIcon } from '../../../../../../assets/icons';
import { setAddNewCardModal } from '../../../../../../store/workspace/slice';
import { xhrDefaultMessage } from '../../../../../../configs/notification';
import { setNotification } from '../../../../../../store/general/slice';
import { ListItem } from '../../../../../../store/general/types';
import { AddCardStep } from '../../../types';
import DropDown from '../../../../../../components/DropDown';
import { useDispatch } from '../../../../../../store/store';
import styles from './style.module.scss';
import Input from '../../../../../../components/Input';
import Modal from '../../../../../../components/Modal';
import { countries } from '../../../../../../configs';
import { useState } from 'react';
import {
    getPaymentMethodsThunk,
    attachPaymentMethodThunk,
} from '../../../../../../store/workspace/thunks';

/** Stripe only honours a fixed set of properties here, the rest is styled by us. */
const cardElementStyles = {
    base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'inherit',
        backgroundColor: '#fff',
        padding: '10px 12px',
        '::placeholder': {
            color: '#aab7c4',
        },
    },
};

/** Tokenises a card with Stripe and attaches it to the Stripe customer. */
export default function CardForm() {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [loading, setLoading] = useState(false);
    const [fullName, setFullName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [billingAddress, setBillingAddress] = useState('');
    const [country, setCountry] = useState<ListItem | null>(null);
    const [step, setStep] = useState<AddCardStep>('CreditInfo');

    const isDone = step === 'Done';
    const closeModal = () => dispatch(setAddNewCardModal(false));

    const attachCard = async () => {
        const cardElement = elements?.getElement(CardElement);
        if (!stripe || !cardElement) return;

        setErrorMessage('');
        setLoading(true);

        const result = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
                name: fullName,
                email: '',
                address: {
                    line1: billingAddress,
                    city,
                    state,
                    postal_code: '',
                    country: country?.id,
                },
            },
        });

        if (result.error) {
            setLoading(false);
            setErrorMessage(result.error.message?.split('.')[0] || xhrDefaultMessage);
            dispatch(setNotification([result.error.message || xhrDefaultMessage, 'error']));
            return;
        }

        dispatch(attachPaymentMethodThunk({
            action: 'attach_payment_method',
            pmId: result.paymentMethod.id,
        }))
            .unwrap()
            .then(() => {
                setStep('Done');
                dispatch(getPaymentMethodsThunk({ action: 'get_payment_methods' }));
            })
            .catch(error => {
                setErrorMessage(error?.message || xhrDefaultMessage);
                dispatch(setNotification([xhrDefaultMessage, 'error']));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Modal
            size='sm'
            closeFunc={closeModal}
            childClassName={styles.wrapper}
            actions={isDone ? 'confirm' : 'both'}
            icon={isDone ? <SquareCheckIcon /> : <CreditCardIcon />}
            title={isDone ? 'Card Added' : 'Add new credit or debit card'}
            dismissButton={{
                text: 'Cancel',
                type: 'Outline-Primary',
                onClick: closeModal,
            }}
            confirmButton={{
                text: isDone ? 'Confirm' : 'Continue',
                type: 'Primary',
                loading: loading,
                onClick: isDone ? closeModal : attachCard,
                disabled: !stripe || (!isDone && (!fullName || !city || !state || !country)),
            }}
        >
            {isDone ? <span className={styles.successfull}>
                Your card has been successfully added.
            </span> : <form>
                <span className={styles.cardElement_title}>Card Info</span>
                <div className={styles.cardElement}>
                    <CardElement options={{ style: cardElementStyles }} />
                </div>
                <div className={styles.billing}>
                    <Input
                        label=' Full Name or Company Name'
                        placeholder=''
                        type='text'
                        value={fullName}
                        onChange={setFullName}
                    />
                    <Input
                        label='Billing Address'
                        placeholder=''
                        type='text'
                        value={billingAddress}
                        onChange={setBillingAddress}
                    />
                    <div className={styles.billing_city_and_state}>
                        <Input
                            label='City'
                            placeholder=''
                            type='text'
                            value={city}
                            onChange={setCity}
                        />
                        <Input
                            label='State'
                            placeholder=''
                            type='text'
                            value={state}
                            onChange={setState}
                        />
                    </div>
                    <DropDown
                        idKey='id'
                        searchable
                        openListOnTop
                        titleKey='title'
                        label='Country'
                        setItem={setCountry}
                        selectedItem={country}
                        dropDownList={countries}
                    />
                </div>
            </form>}

            {errorMessage && <span className={styles.error}>{errorMessage}</span>}
        </Modal>
    );
};
